/**
 * tailwindClassnames.tsx
 * 
 * This file contains reusable Tailwind CSS class combinations for consistent styling across the application.
 */

// Layout and Container Classes
export const containerClasses = {
    page: "bg-very-light-gray dark:bg-off-black transition-all duration-300",
    card: "bg-white dark:bg-dark-gray shadow rounded-md p-4 transition-all duration-300",
    header: "bg-white dark:bg-off-black shadow static inset-x-0 px-12 pt-5 -mt-5 -mx-5 xl:-mx-12 transition-all duration-300"
};

// Text Classes
export const textClasses = {
    heading: "text-3xl font-bold mb-3 text-off-black dark:text-white",
    subheading: "text-xl font-semibold text-off-black dark:text-white",
    body: "text-off-black dark:text-white",
    label: "text-sm font-medium text-neutral-gray-900 dark:text-gray-lines"
};

// Button Classes
export const buttonClasses = {
    primary: "bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 py-2 transition-colors duration-300",
    secondary: "bg-light-gray hover:bg-gray-lines text-off-black dark:text-white dark:bg-neutral-gray-700 dark:hover:bg-secondary-gray font-bold rounded px-7 py-2 transition-colors duration-300",
    danger: "bg-status-red hover:bg-red-700 text-white font-bold rounded px-7 py-2 transition-colors duration-300",
    link: "text-blue-basic hover:text-blue-700 dark:text-blue-basic dark:hover:text-light-blue-300 underline transition-colors duration-300",
    resetFilter: "w-fit bg-light-green dark:bg-dark-green-900 text-dark-green dark:text-dark-green-100 mt-3 px-3 py-2 rounded hover:bg-light-green-hover dark:hover:bg-dark-green-800 transition-colors duration-300",
    disabled: "bg-light-green dark:bg-neutral-gray-700 text-medium-gray dark:text-neutral-gray-400 font-bold rounded px-7 py-2 cursor-not-allowed transition-colors duration-300",
    textButton: "text-secondary-gray dark:text-gray-lines hover:text-dark-green dark:hover:text-white font-bold transition-colors duration-300"
};

// Table Classes
export const tableClasses = {
    container: "table-auto h-auto w-full border border-gray-lines dark:border-secondary-gray text-off-black dark:text-white rounded-md bg-very-light-gray dark:bg-off-black transition-all duration-300",
    header: "px-3 pt-3 pb-2 font-bold uppercase text-sm bg-light-gray dark:bg-dark-gray text-left transition-all duration-300",
    headerCell: "p-3",
    row: "bg-very-light-gray dark:bg-off-black border border-gray-lines dark:border-secondary-gray hover:bg-light-blue align-top transition-all duration-300",
    cell: "p-3",
    sortIcon: "cursor-pointer",

    // Status styles
    statusHealthy: "flex items-center text-dark-green",
    statusWarning: "flex items-center text-status-orange",
    statusError: "flex items-center text-status-red",
};

// Filter and Search Classes
export const filterClasses = {
    container: "text-off-black bg-very-light-gray dark:bg-off-black dark:text-white mr-3 mb-3 h-auto px-3 pb-3 divide-y divide-gray-lines overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600 transition-all duration-300 w-80 flex-shrink-0",
    section: "my-6 pt-3",
    tag: "text-sm w-fit py-1 px-2 bg-light-green dark:bg-dark-green-900 mt-1 rounded-sm items-end",
    tagCount: "bg-dark-green text-white text-sm py-1 px-3 rounded-full",
    header: "flex flex-row justify-between mt-2 pt-2"
};

// Pagination Classes
export const paginationClasses = {
    container: "w-full mt-2 flex flex-row justify-between bg-very-light-gray dark:bg-off-black opacity-80"
};

// Form Classes
export const formClasses = {
    group: "mb-4",
    label: "block text-sm font-medium text-off-black dark:text-white mb-1",
    input: "w-full p-2 border border-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-dark-green dark:bg-neutral-gray-700 dark:text-white",
    select: "w-full p-2 border border-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-dark-green dark:bg-neutral-gray-700 dark:text-white",
    checkbox: "mr-2 rounded text-dark-green focus:ring-dark-green dark:bg-neutral-gray-700",
    error: "mt-1 text-sm text-status-red"
};

// Modal Classes
export const modalClasses = {
    overlay: "fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 flex justify-center items-center z-50 transition-colors duration-300",
    container: "bg-white dark:bg-dark-gray rounded-md p-6 max-w-lg w-full shadow-xl",
    header: "text-xl font-bold mb-4 text-off-black dark:text-white",
    body: "mb-6 text-off-black dark:text-white",
    footer: "flex justify-end space-x-2"
};

// Status and Notification Classes
export const statusClasses = {
    success: "text-dark-green dark:text-status-green",
    warning: "text-status-orange",
    error: "text-status-red",
    info: "text-blue-basic"
};

// Radio Select List Classes
export const radioSelectClasses = {
    container: "flex flex-col max-w-xs",
    header: "w-full flex flex-row justify-between",
    sectionLabel: "font-semibold text-off-black dark:text-white",
    input: "my-2 p-1.5 border border-gray-lines dark:border-secondary-gray rounded rounded-sm px-2 pr-8 w-full dark:bg-neutral-gray-700 dark:text-white transition-all duration-300",
    clearButton: "absolute right-1.5 top-1/2 transform -translate-y-1/2 text-dark-green dark:text-dark-green-100",
    listContainer: "max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600",
    itemContainer: "flex flex-row",
    radioInput: "accent-dark-green cursor-pointer",
    radioLabel: "inline-block mx-2 text-off-black dark:text-white truncate",
    radioLabelHover: "inline-block hover:cursor-pointer mx-2 text-off-black dark:text-white truncate",
    loadMoreLabel: "text-dark-green dark:text-dark-green-100 text-sm cursor-pointer",
    loadingLabel: "text-medium-gray text-sm cursor-pointer"
};

// Select List Classes (for checkboxes)
export const selectListClasses = {
    container: "flex flex-col max-w-xs",
    header: "w-full flex flex-row justify-between",
    sectionLabel: "font-semibold text-off-black dark:text-white",
    input: "my-2 p-1.5 border border-gray-lines dark:border-secondary-gray rounded rounded-sm px-2 pr-8 w-full dark:bg-neutral-gray-700 dark:text-white transition-all duration-300",
    clearButton: "absolute right-1.5 top-1/2 transform -translate-y-1/2 text-dark-green dark:text-dark-green-100",
    listContainer: "max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600",
    itemContainer: "flex flex-row",
    checkboxInput: "accent-dark-green cursor-pointer",
    checkboxLabel: "inline-block mx-2 text-off-black dark:text-white truncate",
    checkboxLabelHover: "inline-block hover:cursor-pointer mx-2 text-off-black dark:text-white truncate",
    loadMoreLabel: "text-dark-green dark:text-dark-green-100 text-sm cursor-pointer",
    loadingLabel: "text-medium-gray text-sm cursor-pointer"
};

// Connection Status Classes
export const connectionStatusClasses = {
    // Base styles that are shared across all status indicators
    baseStatus: (detailsPage: boolean) => `${detailsPage ? 'font-semibold px-2 py-1' : 'text-xs'} text-center rounded rounded-sm transition-all duration-300`,

    // Specific status styles with dark mode support
    healthy: (detailsPage: boolean) => `${detailsPage ? 'font-semibold px-2 py-1' : 'text-xs'} text-center rounded rounded-sm bg-light-green dark:bg-accent-green2 text-dark-green dark:text-grey-g0 transition-all duration-300`,
    unhealthy: (detailsPage: boolean) => `${detailsPage ? 'px-2 py-1' : 'text-xs'} text-center rounded rounded-sm bg-very-light-orange dark:bg-accent-red2 text-orange dark:text-grey-g0 transition-all duration-300`,
    needsReview: (detailsPage: boolean) => `${detailsPage ? 'font-semibold px-2 py-1' : 'text-xs'} text-center rounded rounded-sm bg-warning-yellow dark:bg-accent-amber2 text-warning-dark-yellow dark:text-grey-g0 transition-all duration-300`,
    disabled: (detailsPage: boolean) => `${detailsPage ? 'font-semibold px-2 py-1' : 'text-xs'} text-center rounded rounded-sm bg-light-gray dark:bg-grey-g7 text-medium-gray dark:text-grey-g0 transition-all duration-300`,
    unknown: (detailsPage: boolean) => `${detailsPage ? 'font-semibold px-2 py-1' : 'text-xs'} text-center rounded rounded-sm bg-light-gray dark:bg-grey-g7 text-medium-gray dark:text-grey-g0 transition-all duration-300`,
};

// Connection Details Classes
export const connectionDetailsClasses = {
    container: "relative h-full",
    header: "bg-white dark:bg-neutral-gray-900 shadow static inset-x-0 px-12 pt-5 -mt-5 -mx-5 xl:-mx-12 transition-all duration-300",
    title: "text-3xl font-semibold text-off-black dark:text-white",
    infoLabel: "text-secondary-gray dark:text-gray-lines mt-2 mb-3 mr-1 text-sm",
    infoPill: "text-secondary-gray dark:text-gray-lines mt-2 mb-3 bg-light-gray dark:bg-neutral-gray-700 rounded rounded-sm px-2 py-1 mr-3 text-sm",
    separator: "text-secondary-gray dark:text-gray-lines mt-2 mb-3 mr-2",
    enabledPanel: "flex flex-row items-center p-4 gap-4 w-fit transition-all duration-300",
    disabledPanel: "flex flex-row items-center p-4 gap-4 w-fit bg-light-gray-button dark:bg-neutral-gray-700 transition-all duration-300",
    tabActive: "border-2 border-white dark:border-neutral-gray-900 border-b-status-green dark:border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-all duration-300",
    tabInactive: "border-2 border-white dark:border-neutral-gray-900 mr-3 py-3 cursor-pointer text-medium-gray dark:text-gray-lines transition-all duration-300",
    switchText: "font-semibold text-off-black dark:text-white flex flex-row items-center",
    switchTextLabel: "mr-2",
    switchContainer: "group relative flex h-7 w-14 cursor-pointer rounded-full p-1 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 data-[focus]:outline-1 data-[focus]:outline-white"
};// Text Input Classes
export const inputClasses = {
    standard: "p-1.5 h-auto border border-neutral-gray-200 dark:border-secondary-gray rounded text-off-black dark:text-white dark:bg-neutral-gray-700 transition-all duration-300",
    disabled: "p-1.5 h-auto border border-neutral-gray-200 dark:border-secondary-gray rounded bg-light-gray dark:bg-neutral-gray-700 text-medium-gray dark:text-neutral-gray-400 transition-all duration-300",
    error: "p-1.5 h-auto border border-orange dark:border-orange rounded text-off-black dark:text-white dark:bg-neutral-gray-700 transition-all duration-300"
};

// Global Search Classes
export const globalSearchClasses = {
    container: "flex flex-col max-w-xs items-start transition-all duration-300",
    inputContainer: "flex flex-row",
    inputWrapper: "relative",
    input: "p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 pr-8 dark:bg-neutral-gray-700 dark:text-white transition-all duration-300",
    clearButton: "absolute right-0 top-0 mt-1.5 mr-1 text-dark-green dark:text-dark-green-100 transition-all duration-300",
    searchButton: "mx-2 mb-2 bg-dark-green hover:bg-status-green text-white rounded text-sm p-2 h-auto transition-all duration-300"
};/**
 * Helper function to combine class names
 * @param classes - Array of class names
 * @returns Combined class names string
 */
export const combineClasses = (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
};

// Example usage of combineClasses:
// const tableHeaderClass = combineClasses(tableClasses.headerCell, sortable && tableClasses.sortIcon);
